/**
 * Training Celebration Utilities (Issue #2177)
 *
 * Provides celebratory effects when users complete training plans.
 * Includes confetti animations that respect accessibility preferences.
 */

import confetti from 'canvas-confetti'

/**
 * Default confetti configuration for training celebrations.
 */
const CELEBRATION_CONFIG = {
  particleCount: 100,
  spread: 70,
  origin: { y: 0.6 },
  colors: [
    '#2563eb', // blue-600
    '#16a34a', // green-600
    '#7c3aed', // violet-600
    '#f59e0b', // amber-500
    '#ec4899', // pink-500
  ],
}

/**
 * Check if the user prefers reduced motion.
 * Returns true if the user has requested reduced motion,
 * or if we can't determine the preference.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') {
    return true // Server-side, assume reduced motion
  }

  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  return mediaQuery.matches
}

/**
 * Check if the document is currently visible.
 * Prevents confetti from running in background tabs.
 */
function isDocumentVisible(): boolean {
  if (typeof document === 'undefined') {
    return false
  }

  return document.visibilityState === 'visible'
}

/**
 * Check if celebration effects should run.
 * Returns false if user prefers reduced motion or tab is not visible.
 */
function shouldCelebrate(): boolean {
  return !prefersReducedMotion() && isDocumentVisible()
}

/**
 * Fire a celebratory confetti animation.
 *
 * Respects user preferences:
 * - Does not fire if user prefers reduced motion
 * - Does not fire if tab is not visible
 *
 * @returns true if confetti was fired, false if skipped
 */
export function celebrateCompletion(): boolean {
  if (!shouldCelebrate()) {
    return false
  }

  // Fire confetti from the center
  confetti({
    ...CELEBRATION_CONFIG,
  })

  // Fire additional bursts for a more celebratory effect
  setTimeout(() => {
    if (!isDocumentVisible()) return

    confetti({
      ...CELEBRATION_CONFIG,
      particleCount: 50,
      origin: { x: 0.3, y: 0.6 },
    })
  }, 150)

  setTimeout(() => {
    if (!isDocumentVisible()) return

    confetti({
      ...CELEBRATION_CONFIG,
      particleCount: 50,
      origin: { x: 0.7, y: 0.6 },
    })
  }, 300)

  return true
}

/**
 * Fire a smaller celebration effect.
 * Useful for completing individual items.
 */
export function celebrateSmall(origin?: { x: number; y: number }): boolean {
  if (!shouldCelebrate()) {
    return false
  }

  confetti({
    particleCount: 30,
    spread: 50,
    origin: origin ?? { y: 0.7 },
    colors: CELEBRATION_CONFIG.colors,
  })

  return true
}

/**
 * Reset the confetti canvas.
 * Call this when unmounting or navigating away.
 */
export function resetCelebration(): void {
  confetti.reset()
}
